import { apiAddBookingReview } from 'app/api/client-bookings/client-bookings';
import axios from 'axios';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import {
  apiApproveAndPayReport,
  apiGetReport,
} from '../api/client-reports/client-reports';
import { AppThunk } from './index';

export interface ClientReportsState {
  page: number;
  perPage: number;
  search: string;
  totalEntries: number;
  items: Array<AppNS.JobEventReport>;
  currentReport: AppNS.JobEventReport | null;
  isReportLoading: boolean;
  isSharingInProgress: boolean;
  isReportLoadingError: boolean;
  isDeleteEventLoading: boolean;
  isDeleteEventError: boolean;
  isReportStateLoadingError: boolean;
  isReportStateLoading: boolean;

  isReportValid: boolean;
  reportValidErrors: Array<string> | null;
  isRequestLoading: boolean;
  isRequestLoadingError: boolean;

  isCancellingJob: boolean;
  isCancellingJobError: boolean;

  detailsActionDone: boolean;

  isTimeLoading: boolean;
  isTimeLoadingError: boolean;

  isNotesLoading: boolean;
  isNotesLoadingError: boolean;

  isListItemLoading: boolean;
  isListItemLoadingError: boolean;
  isReportFinalizing: boolean;

  isDetailsPageTouched: boolean;
  isAddingChecklist: boolean;
  isAddingChecklistError: boolean;

  isRemovingChecklist: boolean;
  isRemovingChecklistError: boolean;

  paymentMethodId: string;
  paymentMethodNotRequired: boolean;

  isAddingReview: boolean;
  isAddingReviewError: boolean;
}

const initialState: ClientReportsState = {
  page: 1,
  perPage: 10,
  search: '',
  currentReport: null,
  totalEntries: 0,
  items: [],
  isReportLoading: false,
  isSharingInProgress: false,
  isReportLoadingError: false,
  isDeleteEventLoading: false,
  isDeleteEventError: false,
  isReportStateLoadingError: false,
  isReportStateLoading: false,
  isReportValid: false,
  reportValidErrors: null,
  isRequestLoading: false,
  isRequestLoadingError: false,
  isCancellingJob: false,
  isCancellingJobError: false,
  detailsActionDone: false,
  isTimeLoading: false,
  isTimeLoadingError: false,
  isNotesLoading: false,
  isNotesLoadingError: false,
  isListItemLoading: false,
  isListItemLoadingError: false,
  isReportFinalizing: false,
  isDetailsPageTouched: false,
  isAddingChecklist: false,
  isAddingChecklistError: false,
  isRemovingChecklist: false,
  isRemovingChecklistError: false,
  isAddingReview: false,
  isAddingReviewError: false,
  paymentMethodId: '',
  paymentMethodNotRequired: false,
};

const clientReports = createSlice({
  name: 'clientReports',
  initialState,
  reducers: {
    getReportStart(state: ClientReportsState) {
      state.currentReport = null;
      state.isReportLoading = true;
      state.isReportLoadingError = false;
      state.isSharingInProgress = false;
    },
    getCurrentReportSuccess(
      state: ClientReportsState,
      action: PayloadAction<AppNS.JobEventReport>
    ) {
      state.isReportLoadingError = false;
      state.isReportLoading = false;
      state.reportValidErrors = null;
      state.currentReport = action.payload;
      state.paymentMethodId = state.currentReport.booking.paymentMethodId || '';
    },
    getReportFailed(state: ClientReportsState) {
      state.isReportLoadingError = true;
      state.isReportLoading = false;
    },
    updateCurrentReportSharingStart(state: ClientReportsState) {
      state.isReportLoading = false;
      state.isSharingInProgress = true;
      state.isReportLoadingError = false;
    },
    updateCurrentReportSharingSuccess(
      state: ClientReportsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.isReportLoadingError = false;
      state.isReportLoading = false;
      state.isSharingInProgress = false;
      if (state.currentReport) {
        state.currentReport = {
          ...state.currentReport,
          shareUuid: action.payload.properties.shareUuid || null,
        };
      }
      checkReportValid();
    },
    setReportStateStart(state: ClientReportsState) {
      state.isReportStateLoading = true;
    },
    setReportStateSuccess(
      state: ClientReportsState,
      action: PayloadAction<AppNS.JobReportState>
    ) {
      state.isReportStateLoading = false;
      if (state.currentReport) {
        state.currentReport.state = action.payload;
      }
    },
    setReportStateFailed(state: ClientReportsState) {
      state.isReportStateLoadingError = true;
      state.isReportStateLoading = false;
    },
    setDetailsActionDone(
      state: ClientReportsState,
      action: PayloadAction<boolean>
    ) {
      state.detailsActionDone = action.payload;
      state.isReportValid = false;
    },
    setDetailsPageTouched(
      state: ClientReportsState,
      action: PayloadAction<boolean>
    ) {
      state.isDetailsPageTouched = action.payload;
    },
    setFinalizeStart(state: ClientReportsState) {
      state.isReportFinalizing = true;
      state.reportValidErrors = null;
    },
    setFinalizeSuccess(state: ClientReportsState) {
      state.isReportFinalizing = false;
      if (state.currentReport) {
        state.currentReport.state = 'finalized_report';
      }
    },
    setReportState(
      state: ClientReportsState,
      action: PayloadAction<AppNS.JobReportState>
    ) {
      state.isReportFinalizing = false;
      if (state.currentReport) {
        state.currentReport.state = action.payload;
      }
    },
    setFinalizeFailed(
      state: ClientReportsState,
      action: PayloadAction<string[]>
    ) {
      state.isReportFinalizing = false;
      state.reportValidErrors = action.payload;
    },
    setPaymentMethodId(
      state: ClientReportsState,
      action: PayloadAction<string>
    ) {
      state.paymentMethodId = action.payload;
      clientReports.caseReducers.checkReportValid(state);
    },
    setPaymentMethodNotRequired(
      state: ClientReportsState,
      action: PayloadAction<boolean>
    ) {
      state.paymentMethodNotRequired = action.payload;
    },
    addReviewStart(state: ClientReportsState) {
      state.isAddingReview = true;
    },
    addReviewSuccess(
      state: ClientReportsState,
      action: PayloadAction<AppNS.Review>
    ) {
      state.isAddingReview = false;
      if (state.currentReport) {
        state.currentReport.review = action.payload;
      }
    },
    addReviewFailed(state: ClientReportsState) {
      state.isAddingReviewError = true;
      state.isAddingReview = false;
    },
    checkReportValid(state: ClientReportsState) {
      const report = state.currentReport;
      if (!report) {
        state.isReportValid = false;
        return;
      }
      state.reportValidErrors = null;
      if (!state.paymentMethodId && !state.paymentMethodNotRequired) {
        state.reportValidErrors = [
          'Please pick or add some credit card or bank account',
        ];
      }
      state.isReportValid = !state.reportValidErrors;
    },
  },
});

export const {
  getCurrentReportSuccess,
  getReportFailed,
  getReportStart,
  setPaymentMethodId,
  setDetailsActionDone,
  setDetailsPageTouched,
  setFinalizeFailed,
  setFinalizeStart,
  setFinalizeSuccess,

  updateCurrentReportSharingStart,
  updateCurrentReportSharingSuccess,

  setReportStateFailed,
  setReportStateStart,
  setReportStateSuccess,
  setPaymentMethodNotRequired,
  setReportState,

  addReviewStart,
  addReviewSuccess,
  addReviewFailed,
  checkReportValid,
} = clientReports.actions;

export default clientReports.reducer;

export const getReport =
  (id: string, isSilent: boolean): AppThunk =>
  async (dispatch) => {
    try {
      if (!isSilent) {
        dispatch(getReportStart());
      }

      const result = await apiGetReport(id);
      dispatch(getCurrentReportSuccess(result));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getReportFailed());
    }
  };

export const finalizeReport =
  (id: string, paymentMethodId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setFinalizeStart());

      const result = await apiApproveAndPayReport(id, paymentMethodId);
      dispatch(setReportState(result.state));
      dispatch(setDetailsActionDone(true));
    } catch (err) {
      dispatch(setFinalizeFailed(err as string[]));
    }
  };

export const addReview =
  (eventReportId: string, body: string, rating: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addReviewStart());
      const id = await apiAddBookingReview({
        properties: { eventReportId, body, rating },
      });

      dispatch(addReviewSuccess({ id, body, rating }));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(addReviewFailed());
    }
  };
